import {
  Box, Grid, Card, CardContent, Typography, Chip,
  Stack, CircularProgress, Divider,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  WarningAmber, LocalShipping, Speed, CheckCircle,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { useDevices } from '../../hooks/useDevices'
import { useAlerts } from '../../hooks/useRules'
import { useAuthStore } from '../../store/authStore'

const SEVERITY_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  critical: { bg: '#fff1f2', color: '#ef4444', border: '#fecaca' },
  warning:  { bg: '#fffbeb', color: '#f59e0b', border: '#fde68a' },
  info:     { bg: '#eff6ff', color: '#3b82f6', border: '#bfdbfe' },
}

export default function DriverDashboardPage() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const { data: devicesRaw, isLoading: devicesLoading } = useDevices()
  const { data: alerts = [], isLoading: alertsLoading } = useAlerts({ limit: 50 })

  // useDevices returns the API wrapper { devices: [...] } cast as Device[]
  const devices = Array.isArray(devicesRaw)
    ? devicesRaw
    : ((devicesRaw as { devices?: unknown[] } | undefined)?.devices ?? [])

  if (devicesLoading || alertsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#34A8E0' }} />
      </Box>
    )
  }

  const onlineCount = devices.filter((d: any) => d.status === 'online').length
  const recentAlerts = alerts.slice(0, 8)

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
          {t('driver.title', { name: user?.name || t('driver.defaultName') })}
        </Typography>
        <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
          {t('driver.assignedDevicesSubtitle')}
        </Typography>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight={600}>{t('driver.assignedDevices')}</Typography>
                  <Typography variant="h3" fontWeight={800} color="#0f172a" sx={{ lineHeight: 1.1, mt: 0.5 }}>
                    {devices.length}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 44, height: 44, borderRadius: 2, bgcolor: '#EBF6FD',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <LocalShipping sx={{ color: '#34A8E0' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight={600}>{t('driver.onlineDevices')}</Typography>
                  <Typography variant="h3" fontWeight={800} color="#0f172a" sx={{ lineHeight: 1.1, mt: 0.5 }}>
                    {onlineCount}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 44, height: 44, borderRadius: 2, bgcolor: '#f0fdf4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CheckCircle sx={{ color: '#10b981' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="caption" color="#64748b" fontWeight={600}>{t('driver.recentAlerts')}</Typography>
                  <Typography variant="h3" fontWeight={800} color="#0f172a" sx={{ lineHeight: 1.1, mt: 0.5 }}>
                    {alerts.length}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 44, height: 44, borderRadius: 2, bgcolor: '#fffbeb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <WarningAmber sx={{ color: '#f59e0b' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* Devices */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} color="#0f172a" sx={{ fontSize: 15, mb: 2 }}>
                {t('driver.myDevices')}
              </Typography>
              <Stack spacing={1}>
                {devices.length === 0 && (
                  <Typography variant="body2" color="#94a3b8" sx={{ py: 3, textAlign: 'center' }}>
                    {t('driver.noDevicesAssigned')}
                  </Typography>
                )}
                {devices.map((d: any) => (
                  <Box key={d.id} sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    p: 1.25, borderRadius: 1.5, bgcolor: '#fafafa', border: '1px solid #f1f5f9',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        bgcolor: d.status === 'online' ? '#10b981' : '#94a3b8',
                      }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600} color="#1e293b">{d.name}</Typography>
                        <Typography variant="caption" color="#94a3b8">{d.device_identifier}</Typography>
                      </Box>
                    </Box>
                    <Chip label={d.status} size="small" sx={{
                      fontSize: 10, fontWeight: 600, textTransform: 'capitalize',
                      bgcolor: d.status === 'online' ? '#f0fdf4' : '#f8fafc',
                      color: d.status === 'online' ? '#10b981' : '#94a3b8',
                      border: `1px solid ${d.status === 'online' ? '#bbf7d0' : '#e2e8f0'}`,
                    }} />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent alerts */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} color="#0f172a" sx={{ fontSize: 15 }}>
                  {t('driver.recentAlerts')}
                </Typography>
                <Speed sx={{ fontSize: 18, color: '#94a3b8' }} />
              </Box>
              <Stack spacing={1}>
                {recentAlerts.length === 0 && (
                  <Typography variant="body2" color="#94a3b8" sx={{ py: 3, textAlign: 'center' }}>
                    {t('driver.noAlerts')}
                  </Typography>
                )}
                {recentAlerts.map((a: any) => {
                  const s = SEVERITY_STYLE[a.severity] ?? SEVERITY_STYLE.info
                  return (
                    <Box key={a.id} sx={{
                      p: 1.25, borderRadius: 1.5, border: '1px solid #f1f5f9', bgcolor: '#fafafa',
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="body2" color="#1e293b" sx={{ flex: 1 }}>{a.message}</Typography>
                        <Chip label={a.severity} size="small" sx={{
                          fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                          bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`,
                        }} />
                      </Box>
                      <Divider sx={{ my: 0.75, borderColor: '#f1f5f9' }} />
                      <Typography variant="caption" color="#94a3b8">
                        {a.created_at ? format(new Date(a.created_at), 'MMM d, HH:mm') : '—'}
                      </Typography>
                    </Box>
                  )
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
